// The cued round: the app cues a sound, the participant triggers it on the
// current interface, and one row is written per trial.
//
// The round never reaches into an interface. It uses only the `study` handle
// from the interface contract (cue / clearCue / geometry / onOnset) and the
// command log, which is what keeps a new interface study-able for free.

import { scheduleCue } from './cue.js'
import { assessValidity } from './monitor.js'
import { eventTime, dispatchDelay, nextFrame } from '../clock.js'
import { fittsDesign, cueSequence, indexOfDifficulty } from './counterbalance.js'

export function createCuedRound({ engine, api, params, logger, monitor, metronome, env }) {
  let aborted = false

  // Pick the sound whose pad sits closest to the distance this design cell
  // asks for. The design is nominal; what gets logged is what was measured.
  function chooseTarget(handle, sounds, wantDistance) {
    let best = null
    for (const sound of sounds) {
      const g = handle.study?.geometry?.(sound) ?? {}
      const d = g.pad_distance_px
      if (d == null) continue
      const err = Math.abs(d - wantDistance)
      if (!best || err < best.err) best = { sound, geometry: g, err }
    }
    return best
  }

  async function runTrial({ handle, interfaceId, cueSound, cell, trialNo, block, common }) {
    const guards = monitor.beginTrial()

    let onset = null
    let response = null
    let resolveTrial

    const done = new Promise((r) => { resolveTrial = r })

    const offOnset = handle.study?.onOnset?.((e) => {
      if (onset === null) onset = { t: e.t ?? performance.now() }
    })

    const offCommand = api.onCommand((entry) => {
      if (entry.command !== 'trigger' || response) return
      response = { sound: entry.args[0], ...entry }
      resolveTrial('response')
    })

    // The cue must be PAINTED inside the Draw callback, not before it:
    // cue_visual_ms is derived from that frame, so applying the highlight
    // early would mean the participant saw the cue before the instant we
    // record as its onset, and every reaction time would be short by the lead.
    let cueInfo = {}
    const cue = await scheduleCue({
      engine,
      sound: cueSound,
      cueType: params.get('study.cueType'),
      displayPeriodMs: env.display_period_ms,
      onFlash: () => { cueInfo = handle.study?.cue?.(cueSound) ?? {} },
    })

    // The response window runs from the cue, not from when we started
    // scheduling it -- otherwise the lead time is deducted from the
    // participant's allowance and slow trials time out early.
    const timer = setTimeout(() => resolveTrial('timeout'), params.get('study.cueIntervalMs'))

    // A response that arrived before the cue was painted is a guess, not a
    // response; it is flagged rather than discarded.
    const outcome = await done
    clearTimeout(timer)
    offOnset?.()
    offCommand()
    handle.study?.clearCue?.()

    const g = handle.study?.geometry?.(cueSound) ?? {}
    const timedOut = outcome === 'timeout'
    const onsetMs = onset?.t ?? ''
    const responseMs = response?.t ?? ''
    const hasHome = g.home_cx_px != null && onsetMs !== ''

    const row = {
      ...common,
      interface: interfaceId,
      block,
      trial: trialNo,
      warmup: !!cell?.warmup,
      cue_sound: cueSound,
      cue_modality: params.get('study.cueType'),
      response_sound: response?.sound ?? '',
      correct: response ? response.sound === cueSound : false,
      timed_out: timedOut,

      ...cue,

      onset_ms: onsetMs,
      response_ms: responseMs,
      reaction_time_ms: onsetMs !== '' ? onsetMs - cue.cue_visual_ms : '',
      movement_time_ms: hasHome && responseMs !== '' ? responseMs - onsetMs : '',
      response_time_ms: responseMs !== '' ? responseMs - cue.cue_visual_ms : '',
      has_home: hasHome,
      ts_source: response?.tsSource ?? (response ? 'event' : ''),
      dispatch_delay_ms: response?.dispatchDelayMs ?? '',

      // geometry as measured on screen, not as requested by the design
      nominal_width_px: cell?.width ?? '',
      nominal_distance_px: cell?.distance ?? '',
      pad_width_px: g.pad_width_px ?? '',
      pad_distance_px: g.pad_distance_px ?? '',
      index_of_difficulty:
        g.pad_width_px && g.pad_distance_px ? indexOfDifficulty(g.pad_distance_px, g.pad_width_px) : '',
      target_cx_px: g.target_cx_px ?? '',
      target_cy_px: g.target_cy_px ?? '',
      home_cx_px: g.home_cx_px ?? '',
      home_cy_px: g.home_cy_px ?? '',
      hit_x_px: response?.hit_x_px ?? '',
      hit_y_px: response?.hit_y_px ?? '',
      region: g.region ?? '',

      // interface-specific extras ride in on the trigger's opts object
      pointer_type: response?.pointer_type ?? '',
      pointer_cancelled: !!response?.pointer_cancelled,
      multi_touch: !!response?.multi_touch,
      down_before_cue: !!(response?.down_before_cue ?? cueInfo.down_before_cue),
      hand_in_target_at_cue: !!response?.hand_in_target_at_cue,
      dwell_ms: response?.dwellMs ?? params.get('camera.dwellMs'),
      cam_ts_source: response?.cam_ts_source ?? '',
      cam_period_ms: response?.cam_period_ms ?? '',
      cam_infer_ms: response?.cam_infer_ms ?? '',
      typed_string: response?.typed_string ?? '',
      n_keys: response?.n_keys ?? '',
      n_backspaces: response?.n_backspaces ?? '',
      autocomplete_used: response?.autocomplete_used ?? '',
      hidden_labels: params.get('cli.hiddenLabels'),

      sensor_lag_class: interfaceId === 'camera' ? 'sampled' : 'event',
      sensor_period_ms: interfaceId === 'camera' ? (response?.cam_period_ms ?? '') : 0,

      ...monitor.endTrial(),
      ...(responseMs !== '' ? metronome.asynchronyFor(responseMs, engine.tempo) : {}),
    }

    Object.assign(row, assessValidity(row))
    logger.trial(row)
    return row
  }

  return {
    abort() { aborted = true },

    // One interface, one block of trials.
    async run({ handle, interfaceId, block = 'main', common = {} }) {
      aborted = false
      const rows = []
      const sounds = api.sounds
      const n = params.get('study.trials')
      const isPads = interfaceId === 'pads'

      // Pads get the Fitts design; everything else gets a balanced cue order.
      // Free play has no home pad; a study round needs one, or movement time
      // cannot be separated from reaction time.
      handle.study?.setHome?.(true)

      const cells = isPads ? fittsDesign(logger.participantId, { reps: 2, warmup: 2 }) : null
      const cues = isPads ? null : cueSequence(logger.participantId, sounds, n)
      const total = isPads ? cells.length : cues.length

      for (let i = 0; i < total && !aborted; i++) {
        const cell = cells?.[i]
        let cueSound
        if (isPads) {
          // Resize first, let the layout settle, then measure and choose.
          params.set('pads.size', cell.width)
          await nextFrame()
          await nextFrame()
          cueSound = chooseTarget(handle, sounds, cell.distance)?.sound ?? sounds[i % sounds.length]
        } else {
          cueSound = cues[i]
        }

        rows.push(await runTrial({
          handle,
          interfaceId,
          cueSound,
          cell,
          trialNo: i + 1,
          block: cell?.warmup ? 'warmup' : block,
          common,
        }))

        // Inter-trial gap so the next cue is not on top of the last response.
        await new Promise((r) => setTimeout(r, 400))
      }

      handle.study?.setHome?.(null)
      return rows
    },
  }
}

// Attach the timing provenance the command API cannot know about. Interfaces
// pass raw events through `opts`; this normalises them.
export function describeResponseEvent(event) {
  const { t, source } = eventTime(event)
  return { t, tsSource: source, dispatchDelayMs: dispatchDelay(event) }
}
